import type { RedoAdminRpcClient } from "@redotech/admin-sdk/rpc/client";
import { createAdminRpcClient } from "@redotech/admin-sdk/rpc/client";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { StorefrontPreviewTokenContext } from "@redotech/redo-web/selector-picker/preview-token";
import { Context, createContext, ReactNode, useMemo } from "react";
import { getTargetAddressSpace } from "./target-address-space";

export const RedoAdminRpcClientContext: Context<
  RedoAdminRpcClient | undefined
> = createContext<RedoAdminRpcClient | undefined>(undefined);
RedoAdminRpcClientContext.displayName = "RedoAdminRpcClientContext";

export function RedoAdminRpcClientProvider({
  children,
  baseUrl,
  authToken,
}: {
  children: ReactNode | ReactNode[];
  baseUrl: string;
  authToken?: string;
}) {
  const redoAdminRpcClient = useMemo(() => {
    if (!authToken) {
      return undefined;
    }
    const targetAddressSpace = getTargetAddressSpace(baseUrl);
    return createAdminRpcClient({
      baseURL: new URL(
        `${baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl}/rpc`,
      ),
      headers: { Authorization: `Bearer ${authToken}` },
      ...(targetAddressSpace && { targetAddressSpace }),
    });
  }, [baseUrl, authToken]);
  return (
    <RedoAdminRpcClientContext.Provider value={redoAdminRpcClient}>
      {redoAdminRpcClient ? (
        <AdminStorefrontPreviewTokenProvider>
          {children}
        </AdminStorefrontPreviewTokenProvider>
      ) : (
        children
      )}
    </RedoAdminRpcClientContext.Provider>
  );
}

function AdminStorefrontPreviewTokenProvider({
  children,
}: {
  children: ReactNode;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const getPreviewToken = useHandler(
    async () => (await rpcClient.getStorefrontPreviewToken({})).token,
  );
  return (
    <StorefrontPreviewTokenContext.Provider value={getPreviewToken}>
      {children}
    </StorefrontPreviewTokenContext.Provider>
  );
}
